import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency, formatMoney } from '../../utils/formatters';

export default function TransferModal({ onClose }) {
  const { state, dispatch } = useApp();
  const accounts = state.accounts.filter(a => a.active);
  const [form, setForm] = useState({
    fromId: accounts[0]?.id || '',
    toId: accounts[1]?.id || '',
    value: '',
    toValue: '',
    date: new Date().toISOString().slice(0, 10),
    description: 'Transferência entre contas',
  });
  const [error, setError] = useState('');
  // Depois que o usuário digita o valor recebido manualmente, paramos de
  // recalcular a sugestão pelo câmbio (a taxa da corretora dele é outra)
  const [toTouched, setToTouched] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fromAcc = accounts.find(a => a.id === form.fromId);
  const toAcc = accounts.find(a => a.id === form.toId);
  const fromCur = fromAcc?.currency === 'USD' ? 'USD' : 'BRL';
  const toCur = toAcc?.currency === 'USD' ? 'USD' : 'BRL';
  const crossCurrency = fromCur !== toCur;
  const rate = state.usdRate;

  // Sugestão de valor recebido pelo câmbio do dia (BRL→USD divide, USD→BRL multiplica)
  const suggestTo = (v) => {
    const n = parseFloat(v) || 0;
    if (!n) return '';
    return (fromCur === 'BRL' ? n / rate : n * rate).toFixed(2);
  };

  const onValueChange = (v) => {
    setForm(f => ({ ...f, value: v, toValue: crossCurrency && !toTouched ? suggestTo(v) : f.toValue }));
  };

  const onAccountChange = (k, id) => {
    setToTouched(false);
    setForm(f => {
      const next = { ...f, [k]: id };
      const nf = accounts.find(a => a.id === next.fromId);
      const nt = accounts.find(a => a.id === next.toId);
      const cross = (nf?.currency === 'USD' ? 'USD' : 'BRL') !== (nt?.currency === 'USD' ? 'USD' : 'BRL');
      if (cross && next.value) {
        const n = parseFloat(next.value) || 0;
        next.toValue = n ? ((nf?.currency === 'USD' ? n * rate : n / rate)).toFixed(2) : '';
      }
      return next;
    });
  };

  if (accounts.length < 2) {
    return (
      <div className="modal-backdrop">
        <div className="modal">
          <div className="modal-handle"></div>
          <div className="modal-title"><span><i className="fas fa-arrow-right-arrow-left" style={{ marginRight: 8, color: 'var(--blue)' }}></i>Transferência</span><button className="close-btn" onClick={onClose}>×</button></div>
          <div style={{ textAlign: 'center', padding: '24px 0 12px' }}>
            <i className="fas fa-triangle-exclamation" style={{ fontSize: 36, color: 'var(--gold)', marginBottom: 14, display: 'block' }}></i>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Mínimo de 2 contas necessário</div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Cadastre pelo menos duas contas bancárias para realizar transferências.</div>
          </div>
        </div>
      </div>
    );
  }

  const overdraft = fromAcc?.overdraft || 0;
  const available = (fromAcc?.balance || 0) + overdraft;

  const save = () => {
    setError('');
    if (!form.value || parseFloat(form.value) <= 0) { setError('Informe um valor válido.'); return; }
    if (!form.toId) { setError('Selecione a conta de destino.'); return; }
    if (form.fromId === form.toId) { setError('Selecione contas diferentes.'); return; }
    const val = parseFloat(form.value);
    const toVal = crossCurrency ? parseFloat(form.toValue) : undefined;
    if (crossCurrency && (!toVal || toVal <= 0)) { setError(`Informe quanto chegou na conta de destino (${toCur === 'USD' ? 'US$' : 'R$'}).`); return; }
    if (fromAcc && val > available) {
      const msg = overdraft > 0
        ? `Limite excedido. Disponível com cheque especial: ${formatMoney(available, fromCur)}`
        : `Saldo insuficiente na conta de origem. Disponível: ${formatMoney(fromAcc.balance, fromCur)}`;
      setError(msg);
      return;
    }
    dispatch({ type: 'ADD_TRANSFER', payload: { fromId: form.fromId, toId: form.toId, value: val, toValue: toVal, date: form.date, description: form.description } });
    dispatch({ type: 'ADD_NOTIFICATION', payload: { title: 'Transferência realizada', message: `${formatMoney(val, fromCur)} de ${fromAcc?.name} → ${crossCurrency ? formatMoney(toVal, toCur) + ' em ' : ''}${toAcc?.name}`, type: 'success' } });
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-handle"></div>
        <div className="modal-title">
          <span><i className="fas fa-arrow-right-arrow-left" style={{ marginRight: 8, color: 'var(--blue)' }}></i>Transferência</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="form-group">
          <label className="form-label">Conta de origem</label>
          <select className="form-control" value={form.fromId} onChange={e => onAccountChange('fromId', e.target.value)}>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {formatMoney(a.balance, a.currency)}</option>)}
          </select>
        </div>

        <div className="transfer-arrow"><i className="fas fa-arrow-down"></i></div>

        <div className="form-group">
          <label className="form-label">Conta de destino</label>
          <select className="form-control" value={form.toId} onChange={e => onAccountChange('toId', e.target.value)}>
            {accounts.filter(a => a.id !== form.fromId).map(a => <option key={a.id} value={a.id}>{a.name} — {formatMoney(a.balance, a.currency)}</option>)}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Valor {crossCurrency ? 'enviado' : ''} ({fromCur === 'USD' ? 'US$' : 'R$'}) *</label>
            <input className="form-control" type="number" placeholder="0,00" step="0.01" min="0" value={form.value} onChange={e => onValueChange(e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Data</label>
            <input className="form-control" type="date" value={form.date} onChange={e => set('date', e.target.value)} />
          </div>
        </div>

        {crossCurrency && (
          <div className="form-group">
            <label className="form-label">Valor recebido ({toCur === 'USD' ? 'US$' : 'R$'}) *</label>
            <input className="form-control" type="number" placeholder="0,00" step="0.01" min="0" value={form.toValue} onChange={e => { setToTouched(true); set('toValue', e.target.value); }} />
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              Sugerido pelo câmbio do dia (R$ {rate.toFixed(2).replace('.', ',')}) — ajuste pro valor que realmente chegou, a taxa do banco/corretora costuma ser diferente.
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Descrição</label>
          <input className="form-control" type="text" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>

        {fromAcc && form.value && (() => {
          const val = parseFloat(form.value) || 0;
          const after = fromAcc.balance - val;
          const insufficient = val > available;
          const usingOverdraft = !insufficient && val > fromAcc.balance && overdraft > 0;
          const toVal = parseFloat(form.toValue) || 0;
          const effRate = crossCurrency && val > 0 && toVal > 0 ? (fromCur === 'BRL' ? val / toVal : toVal / val) : null;
          return (
            <div style={{ borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 12, fontSize: 12, background: insufficient ? 'rgba(226,75,74,0.08)' : usingOverdraft ? 'rgba(var(--gold-rgb),0.08)' : 'var(--surface2)', border: insufficient ? '1px solid rgba(226,75,74,0.35)' : usingOverdraft ? '1px solid rgba(var(--gold-rgb),0.35)' : '1px solid transparent' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)' }}>Saldo em conta:</span>
                <strong style={{ color: 'var(--text)' }}>{formatMoney(fromAcc.balance, fromCur)}</strong>
              </div>
              {overdraft > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ color: 'var(--muted)' }}>Cheque especial:</span>
                  <strong style={{ color: 'var(--text)' }}>+ {formatCurrency(overdraft)}</strong>
                </div>
              )}
              {overdraft > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--muted)' }}>Limite disponível:</span>
                  <strong style={{ color: 'var(--text)' }}>{formatMoney(available, fromCur)}</strong>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span style={{ color: insufficient ? 'var(--red)' : 'var(--muted)' }}>Saldo após transferência:</span>
                <strong style={{ color: insufficient ? 'var(--red)' : after < 0 ? 'var(--gold)' : 'var(--green)' }}>{formatMoney(after, fromCur)}</strong>
              </div>
              {crossCurrency && toAcc && toVal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ color: 'var(--muted)' }}>{toAcc.name} após receber:</span>
                  <strong style={{ color: 'var(--green)' }}>{formatMoney(toAcc.balance + toVal, toCur)}</strong>
                </div>
              )}
              {effRate && (
                <div style={{ marginTop: 6, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <i className="fas fa-dollar-sign"></i> Câmbio efetivo: R$ {effRate.toFixed(4).replace('.', ',')} por US$ 1
                </div>
              )}
              {usingOverdraft && (
                <div style={{ marginTop: 6, color: 'var(--gold)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <i className="fas fa-circle-info"></i> Usando cheque especial
                </div>
              )}
              {insufficient && (
                <div style={{ marginTop: 6, color: 'var(--red)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <i className="fas fa-triangle-exclamation"></i> Limite excedido — transferência não permitida
                </div>
              )}
            </div>
          );
        })()}

        {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12, padding: '8px 12px', background: 'rgba(226,75,74,0.08)', borderRadius: 'var(--radius-sm)' }}>{error}</div>}

        <button className="btn btn-blue btn-full" onClick={save} style={{ padding: 12, opacity: parseFloat(form.value) > available ? 0.5 : 1 }}>
          <i className="fas fa-arrow-right-arrow-left"></i> Confirmar transferência
        </button>
      </div>
    </div>
  );
}
